"use client";

import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";

import {
  getPerfSnapshot,
  perf,
  perfEnabled,
  perfLevel,
  subscribePerf,
  type PerfSnapshot,
} from "./canvas-perf";

/** 共享空返回值，避免关闭埋点时每次渲染都分配新数组/对象。 */
const EMPTY_CHANGED: string[] = [];
const EMPTY_WATCH: Record<string, unknown> = Object.freeze({});

/**
 * 统计某个组件的渲染次数（含每次 commit）。放在组件体顶部即可。
 * 关闭埋点时为一次廉价的布尔判断。
 */
export function useRenderTracker(component: string) {
  // 在渲染期间直接计数：memo 化组件每次真正重渲染时都会执行函数体。
  if (perfEnabled()) perf.recordRender(component);
}

/**
 * 重渲染归因：传入“本次想要追踪的字段集合”，与上一次渲染做浅比较，
 * 把发生变化的 key 记入归因表。空变化（memo 失效/父级强制刷新）记为
 * `__no-change__`。这是排查“重复渲染”的核心工具。
 *
 * 用法：useWhyRender("MyComp", { selected: props.selected, url: data.url });
 */
export function useWhyRender(
  component: string,
  watched: Record<string, unknown>,
): string[] {
  const prevRef = useRef<Record<string, unknown> | null>(null);
  if (!perfEnabled()) {
    // 关闭时不维护历史，避免内存占用；重新开启时第一帧记为 mount。
    prevRef.current = null;
    return EMPTY_CHANGED;
  }
  perf.recordRender(component);
  const prev = prevRef.current;
  let changed: string[];
  if (prev === null) {
    changed = ["__mount__"];
    perf.recordRenderReason(component, changed);
  } else {
    changed = [];
    for (const k in watched) {
      if (!Object.is(prev[k], watched[k])) changed.push(k);
    }
    perf.recordRenderReason(component, changed);
  }
  prevRef.current = watched;
  return changed;
}

/**
 * 节点专用归因：精确镜像 `areNodePropsEqual` 的比较口径
 * （id / type / 每个 data.* 字段的引用），告诉你究竟是哪个 data 字段
 * 的引用变化触发了节点重渲染。
 */
export function useWhyNodeRender(component: string, props: any): string[] {
  // Zero-overhead when disabled: this runs for every visible node on each of
  // its re-renders, so don't allocate the watched map / iterate data keys unless
  // perf is actually on. The single `useWhyRender` call below keeps hook order
  // stable regardless of branch.
  let watched: Record<string, unknown> = EMPTY_WATCH;
  if (perfEnabled()) {
    watched = { id: props?.id, type: props?.type };
    const data = props?.data;
    if (data && typeof data === "object") {
      for (const k in data) {
        watched[`data.${k}`] = (data as Record<string, unknown>)[k];
      }
    }
  }
  return useWhyRender(component, watched);
}

/**
 * 回归哨兵：当“本不该发生的渲染”发生时计数 + 节流 console.warn。
 * 典型用途：拖拽期间 canvas-root 重渲染（理想为 0）。
 * @param active   是否处于“此时不应渲染”的窗口（如正在拖拽）
 * @param changed  本次渲染的变化原因（由 useWhyRender 返回）
 * @param ignore   忽略的原因 key（如拖拽起止本身会改的 isNodeDragging）
 */
export function usePerfSentinel(
  name: string,
  active: boolean,
  changed: string[],
  ignore: string[] = [],
) {
  const lastWarnRef = useRef(0);
  if (!perfEnabled() || !active) return;
  const meaningful = changed.filter(
    (k) => k !== "__mount__" && !ignore.includes(k),
  );
  if (meaningful.length === 0) return;
  perf.count(`regression:${name}`);
  perf.event("regression", name, { reasons: meaningful.join(",") });
  const t = performance.now();
  if (t - lastWarnRef.current > 1000) {
    lastWarnRef.current = t;
    // eslint-disable-next-line no-console
    console.warn(
      `[canvas-perf][regression] ${name} — 不应渲染却渲染了，原因:`,
      meaningful,
    );
  }
}

/**
 * 驱动全局 FPS / 长帧统计。整个画布只需挂载一次（HUD 或 canvas-root 内）。
 * 仅在埋点开启时启动 rAF 循环，关闭时不占用主线程。
 */
export function useFrameMeter(active: boolean) {
  useEffect(() => {
    if (!active) return;
    let raf = 0;
    const loop = () => {
      perf.frame();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [active]);
}

/**
 * 订阅埋点开关状态（用于决定是否渲染 HUD / 是否启动 FPS 循环）。
 */
export function usePerfEnabled(): boolean {
  return useSyncExternalStore(
    subscribePerf,
    () => perfEnabled(),
    () => false,
  );
}

/**
 * 订阅当前调试模式等级（0 关闭 / 1 性能监测 / 2 节点检查器）。
 */
export function usePerfLevel(): number {
  return useSyncExternalStore(
    subscribePerf,
    () => perfLevel(),
    () => 0,
  );
}

/**
 * 以固定频率轮询快照（默认 4Hz）供 HUD 渲染，避免高频 setState。
 */
export function usePerfSnapshot(
  enabled: boolean,
  intervalMs = 250,
): PerfSnapshot | null {
  const ref = useRef<PerfSnapshot | null>(null);
  const tick = useRef(0);
  // 用一个简易的外部 store：定时器递增版本号触发重渲染。
  // subscribe 必须保持引用稳定（用 useCallback），否则每次 HUD 重渲染都会
  // 触发 useSyncExternalStore 重新订阅 → 反复 clearInterval/setInterval。
  const subscribe = useCallback(
    (cb: () => void) => {
      if (!enabled) return () => {};
      const id = setInterval(() => {
        ref.current = getPerfSnapshot();
        tick.current += 1;
        cb();
      }, intervalMs);
      return () => clearInterval(id);
    },
    [enabled, intervalMs],
  );
  const version = useSyncExternalStore(
    subscribe,
    () => tick.current,
    () => 0,
  );
  void version;
  if (enabled && !ref.current) ref.current = getPerfSnapshot();
  return ref.current;
}
